import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  CloudIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  HeartIcon,
  ArrowRightIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import cloudMigrationImg from '../assets/img/Recent/cloud migration.png';
import mobileBankingImg from '../assets/img/Recent/Mobilebanking.png';
import analyticsImg from '../assets/img/Recent/data analysis.png';
import healthcareImg from '../assets/img/Recent/Programing.png';

const heroTabs = [
  {
    id: 'solutions',
    label: 'IT Solutions',
    title: 'Innovative IT Solutions',
    subtitle: 'Transforming businesses through cutting-edge technology and strategic digital innovation.',
    cta: 'Explore Services',
    link: '/services',
    glow: 'from-blue-400/30 via-cyan-300/20 to-transparent',
  },
  {
    id: 'security',
    label: 'Cybersecurity',
    title: 'Cybersecurity Excellence',
    subtitle: 'Enterprise-grade protection for your digital assets with advanced threat intelligence.',
    cta: 'Secure Your Business',
    link: '/cybersecurity',
    glow: 'from-emerald-400/25 via-teal-300/15 to-transparent',
  },
  {
    id: 'cloud',
    label: 'Cloud',
    title: 'Cloud Infrastructure',
    subtitle: 'Scalable, resilient cloud architectures built for modern enterprise demands.',
    cta: 'Discover Cloud',
    link: '/cloud-solutions',
    glow: 'from-sky-400/30 via-blue-300/20 to-transparent',
  },
];

const heroStats = [
  { label: 'Projects', value: '51+' },
  { label: 'Uptime', value: '99.9%' },
  { label: 'Experts', value: '21+' },
  { label: 'Satisfaction', value: '98%' },
];

const marqueeTech = [
  'React', 'Node.js', 'AWS', 'Azure', 'Kubernetes', 'Docker',
  'Python', 'TypeScript', 'PostgreSQL', 'Fortinet', 'Cisco', 'Zero Trust',
  'DevOps', 'CI/CD', 'Microservices', 'AI/ML',
];

const enterpriseProjects = [
  {
    id: 1,
    title: 'Enterprise Cloud Migration',
    category: 'Cloud Solutions',
    description: 'Complete migration of legacy systems to scalable cloud infrastructure for Fortune 500 enterprises.',
    image: cloudMigrationImg,
    icon: CloudIcon,
    gradient: 'from-cyan-600 to-blue-800',
    glow: 'shadow-cyan-500/25',
    span: 'md:col-span-2 md:row-span-2',
  },
  {
    id: 2,
    title: 'Secure Banking Platform',
    category: 'Cybersecurity',
    description: 'Highly secure digital banking platform with advanced threat protection and compliance.',
    image: mobileBankingImg,
    icon: ShieldCheckIcon,
    gradient: 'from-emerald-600 to-teal-800',
    glow: 'shadow-emerald-500/25',
    span: 'md:col-span-1',
  },
  {
    id: 3,
    title: 'Smart Retail Analytics',
    category: 'AI & Data',
    description: 'AI-driven analytics increasing retail sales by 27% through predictive insights.',
    image: analyticsImg,
    icon: ChartBarIcon,
    gradient: 'from-violet-600 to-purple-800',
    glow: 'shadow-violet-500/25',
    span: 'md:col-span-1',
  },
  {
    id: 4,
    title: 'Health Management System',
    category: 'Enterprise Solutions',
    description: 'Integrated healthcare management streamlining operations across hospital networks.',
    image: healthcareImg,
    icon: HeartIcon,
    gradient: 'from-rose-600 to-pink-800',
    glow: 'shadow-rose-500/25',
    span: 'md:col-span-2',
  },
];

function FloatingGlow({ className, delay = 0 }) {
  return (
    <motion.div
      className={`absolute rounded-full blur-3xl pointer-events-none ${className}`}
      animate={{
        scale: [1, 1.15, 1],
        opacity: [0.4, 0.7, 0.4],
        x: [0, 24, 0],
        y: [0, -16, 0],
      }}
      transition={{ duration: 10, repeat: Infinity, delay, ease: 'easeInOut' }}
    />
  );
}

function ProjectCard3D({ project, index }) {
  const cardRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) / rect.width);
    y.set((e.clientY - centerY) / rect.height);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const Icon = project.icon;

  return (
    <motion.article
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
      className={`group relative overflow-hidden rounded-2xl ${project.span}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${project.gradient} opacity-90`} />
      <img
        src={project.image}
        alt={project.title}
        className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/95 via-slate-950/40 to-transparent" />

      <div className="relative z-10 flex flex-col justify-end h-full min-h-[280px] md:min-h-[320px] p-6 md:p-8">
        <motion.div
          className="mb-4 w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center"
          whileHover={{ scale: 1.1, rotate: 5 }}
        >
          <Icon className="w-6 h-6 text-white" />
        </motion.div>

        <span className="inline-flex w-fit px-3 py-1 mb-3 text-xs font-semibold tracking-wider uppercase rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90">
          {project.category}
        </span>

        <h3 className="text-xl md:text-2xl font-bold text-white mb-2 group-hover:text-cyan-200 transition-colors">
          {project.title}
        </h3>

        <p className="text-sm md:text-base text-white/70 mb-4 line-clamp-2 md:line-clamp-none max-w-lg">
          {project.description}
        </p>

        <motion.div
          className="flex items-center gap-2 text-cyan-300 text-sm font-medium opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300"
        >
          View Case Study
          <ArrowRightIcon className="w-4 h-4" />
        </motion.div>
      </div>

      <div className={`absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${project.glow} shadow-2xl pointer-events-none`} />
    </motion.article>
  );
}

/**
 * Modern Hero — clean white SaaS aesthetic (Stripe / Linear / Apple style)
 */
export function HeroShowcase() {
  const [activeTab, setActiveTab] = useState(0);
  const active = heroTabs[activeTab];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % heroTabs.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-white via-slate-50/90 to-gray-100/80">
      {/* Soft ambient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-gray-100" />
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className={`absolute top-0 right-0 w-[70%] h-[70%] bg-gradient-to-bl ${active.glow} rounded-full blur-3xl`}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(37,99,235,0.08),transparent)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_100%_50%,rgba(34,211,238,0.06),transparent)]" />
      </div>

      <FloatingGlow className="w-[28rem] h-[28rem] bg-blue-400/15 -top-32 -left-32" delay={0} />
      <FloatingGlow className="w-80 h-80 bg-cyan-300/20 top-1/4 -right-24" delay={2} />
      <FloatingGlow className="w-64 h-64 bg-blue-300/10 bottom-16 left-1/3" delay={4} />

      {/* Subtle floating symbols */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {['</>', '{ }', '0x', 'λ', '☁'].map((symbol, i) => (
          <motion.span
            key={symbol}
            className="absolute text-slate-200/80 font-mono text-xl md:text-3xl font-semibold select-none"
            style={{ left: `${8 + i * 13}%`, top: `${12 + (i % 4) * 22}%` }}
            animate={{ y: [0, -20, 0], opacity: [0.15, 0.35, 0.15] }}
            transition={{ duration: 7 + i, repeat: Infinity, delay: i * 0.4 }}
          >
            {symbol}
          </motion.span>
        ))}
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 md:pt-32 md:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Main content */}
          <div className="lg:col-span-7 space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full hero-glass-tag"
            >
              <SparklesIcon className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm text-slate-600 font-medium tracking-wide">
                Premium IT & Cybersecurity Solutions
              </span>
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45 }}
              >
                <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] font-bold leading-[1.1] tracking-tight mb-6">
                  <span className="block text-slate-900">
                    {active.title.replace(/\s+\S+$/, '')}
                  </span>
                  <span className="bg-gradient-to-r from-[#2563EB] via-blue-500 to-cyan-500 bg-clip-text text-transparent">
                    {active.title.split(' ').slice(-1)[0]}
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-slate-600 max-w-xl mb-10 leading-relaxed font-normal">
                  {active.subtitle}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link
                    to={active.link}
                    className="group inline-flex items-center gap-2 px-8 py-3.5 bg-gradient-to-r from-[#2563EB] to-blue-500 hover:from-blue-600 hover:to-[#2563EB] text-white font-semibold rounded-xl hero-glow transition-all duration-300 hover:-translate-y-0.5"
                  >
                    {active.cta}
                    <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-white/60 backdrop-blur-sm border border-slate-200 text-slate-700 font-medium rounded-xl hover:border-[#2563EB]/40 hover:text-[#2563EB] hover:bg-white hover:shadow-md transition-all duration-300"
                  >
                    Get in Touch
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Tab pills */}
            <div className="flex flex-wrap gap-2 p-1.5 rounded-2xl hero-glass-card w-fit">
              {heroTabs.map((tab, index) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(index)}
                  className={`relative px-5 py-2.5 rounded-xl text-sm font-medium transition-colors duration-300 ${
                    activeTab === index
                      ? 'text-[#2563EB]'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {activeTab === index && (
                    <motion.div
                      layoutId="hero-tab-bg"
                      className="absolute inset-0 bg-white rounded-xl shadow-sm border border-slate-100"
                      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                    />
                  )}
                  <span className="relative z-10">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Glass stat cards */}
          <div className="lg:col-span-5 grid grid-cols-2 gap-4">
            {heroStats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 + i * 0.08 }}
                whileHover={{ y: -4 }}
                className="hero-glass-card p-6 md:p-7 rounded-2xl transition-all duration-300 hover:shadow-[0_12px_40px_-12px_rgba(37,99,235,0.15)] hover:border-blue-200/80 group"
              >
                <p className="text-3xl md:text-4xl font-bold text-slate-900 mb-1 tracking-tight group-hover:text-[#2563EB] transition-colors duration-300">
                  {stat.value}
                </p>
                <p className="text-sm text-slate-500 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Technology marquee — white glass tags */}
        <div className="mt-16 md:mt-24 overflow-hidden rounded-2xl hero-glass-card py-5 px-2">
          <div className="tech-marquee-track flex gap-4 items-center">
            {[...marqueeTech, ...marqueeTech].map((tech, i) => (
              <span
                key={`${tech}-${i}`}
                className="flex-shrink-0 px-4 py-2 text-sm font-medium text-slate-600 hero-glass-tag rounded-xl hover:text-[#2563EB] hover:border-blue-200/60 transition-colors duration-200"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Soft fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none z-10" />
    </section>
  );
}

/**
 * Technologies We Work With — enterprise project showcase
 */
export function TechProjectShowcase() {
  const [activeProject, setActiveProject] = useState(0);
  const scrollRef = useRef(null);

  return (
    <div className="relative">
      {/* Section header */}
      <div className="text-center mb-12">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="inline-block px-4 py-1.5 bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 rounded-full text-sm font-semibold tracking-wider uppercase mb-4 border border-cyan-500/20"
        >
          Enterprise Portfolio
        </motion.span>
        <motion.h3
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4"
        >
          Technologies We Work With
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto"
        >
          Real-world enterprise solutions delivered with cutting-edge technology stacks and security-first architecture.
        </motion.p>
      </div>

      {/* Bento grid — desktop */}
      <div className="hidden md:grid md:grid-cols-3 gap-4 md:gap-6 auto-rows-fr" style={{ perspective: '1200px' }}>
        {enterpriseProjects.map((project, index) => (
          <ProjectCard3D key={project.id} project={project} index={index} />
        ))}
      </div>

      {/* Horizontal scroll — mobile & tablet interactive */}
      <div className="md:hidden relative">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-4 px-1 scrollbar-hide showcase-scroll"
        >
          {enterpriseProjects.map((project, index) => (
            <div key={project.id} className="flex-shrink-0 w-[85vw] max-w-sm snap-center">
              <ProjectCard3D project={{ ...project, span: '' }} index={index} />
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-gray-500 dark:text-gray-400 mt-3">
          Swipe to explore projects →
        </p>
      </div>

      {/* Tab preview strip */}
      <div className="mt-10 flex flex-wrap justify-center gap-3">
        {enterpriseProjects.map((project, index) => (
          <button
            key={project.id}
            onClick={() => setActiveProject(index)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              activeProject === index
                ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/25'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {project.title}
          </button>
        ))}
      </div>

      {/* Active project detail panel */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeProject}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.4 }}
          className="mt-8 p-6 md:p-8 rounded-2xl bg-gradient-to-r from-slate-900/95 to-blue-900/90 backdrop-blur-xl border border-white/10 shadow-2xl"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 ring-2 ring-white/20">
              <img
                src={enterpriseProjects[activeProject].image}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1">
              <span className="text-cyan-400 text-xs font-semibold uppercase tracking-wider">
                {enterpriseProjects[activeProject].category}
              </span>
              <h4 className="text-xl md:text-2xl font-bold text-white mt-1 mb-2">
                {enterpriseProjects[activeProject].title}
              </h4>
              <p className="text-white/70 text-sm md:text-base">
                {enterpriseProjects[activeProject].description}
              </p>
            </div>
            <Link
              to="/portfolio"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-all flex-shrink-0"
            >
              Explore
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Auto-moving tech marquee */}
      <div className="mt-12 overflow-hidden rounded-xl bg-slate-900/50 dark:bg-slate-800/50 border border-gray-200/10 dark:border-white/10 py-3">
        <div className="tech-marquee-reverse flex gap-6">
          {[...marqueeTech, ...marqueeTech].map((tech, i) => (
            <span
              key={`rev-${tech}-${i}`}
              className="flex-shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap"
            >
              ◆ {tech}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Interactive horizontal project showcase — replaces ProjectCarousel
 */
export function ProjectShowcaseScroll() {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    const el = scrollRef.current;
    if (el) el.addEventListener('scroll', checkScroll);
    return () => el?.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const amount = scrollRef.current.clientWidth * 0.85;
    scrollRef.current.scrollBy({ left: direction * amount, behavior: 'smooth' });
  };

  return (
    <div className="relative w-full">
      <div className="absolute inset-y-0 left-0 w-16 md:w-24 bg-gradient-to-r from-white dark:from-gray-900 to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-16 md:w-24 bg-gradient-to-l from-white dark:from-gray-900 to-transparent z-10 pointer-events-none" />

      {canScrollLeft && (
        <button
          onClick={() => scroll(-1)}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-xl border border-gray-200/50 dark:border-gray-700 hover:scale-105 transition-transform hidden sm:flex"
          aria-label="Scroll left"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scroll(1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-xl border border-gray-200/50 dark:border-gray-700 hover:scale-105 transition-transform hidden sm:flex"
          aria-label="Scroll right"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6 pt-2 px-2 scrollbar-hide showcase-scroll"
      >
        {enterpriseProjects.map((project, index) => (
          <motion.div
            key={project.id}
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="flex-shrink-0 w-[300px] sm:w-[340px] snap-start"
          >
            <div className="group relative h-[420px] rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-shadow duration-500">
              <img
                src={project.image}
                alt={project.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className={`absolute inset-0 bg-gradient-to-t ${project.gradient} opacity-80 mix-blend-multiply`} />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />

              <div className="absolute inset-0 p-6 flex flex-col justify-end">
                <span className="w-fit px-3 py-1 mb-3 text-xs font-bold uppercase tracking-wider rounded-full bg-white/15 backdrop-blur-md border border-white/25 text-white">
                  {project.category}
                </span>
                <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                <p className="text-sm text-white/75 mb-4 line-clamp-2">{project.description}</p>
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-2 text-cyan-300 text-sm font-semibold opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300"
                >
                  View Case Study
                  <ArrowRightIcon className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
